import { produce } from 'immer';
import { without } from 'lodash';
import { SET_CLOUD_ASSET_OPTIONS } from 'store/actions';
import { SIGN_OUT } from 'store/auth/actions';
import * as actions from 'store/search/actions';
import { SearchRequest, SearchState, SearchStatus } from 'store/search/types';
import { ActionType, getType, Reducer } from 'typesafe-actions';

const INITIAL_REQUEST: SearchRequest = {
  filter: {
    assessment_statuses: [],
    exclude_assets: [],
    organisations: [],
    search: '',
  },
  limit: 50,
  offset: 0,
  sort: 'relevant:asc',
};

const initialState: SearchState = {
  status: SearchStatus.none,
  opened: false,
  chosen: [],
  lastGoodRequest: INITIAL_REQUEST,
  options: {
    filter: {},
    sort: [],
  },
  request: INITIAL_REQUEST,
  results: {
    asset_ids: [],
    assets: {},
    organisations: {},
    total: null,
  },
};

export const search: Reducer<
  SearchState,
  ActionType<typeof actions | typeof SIGN_OUT.success>
> = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case getType(SET_CLOUD_ASSET_OPTIONS):
        return {
          ...draft,
          opened: true,
          options: {
            ...draft.options,
            sort: action.payload.sort,
            filter: action.payload.filter,
          },
        };

      case getType(actions.SEARCH.request):
        return {
          ...draft,
          error: undefined,
          status: SearchStatus.searching,
          request: {
            ...draft.request,
            offset: action.payload.offset,
            limit: action.payload.limit,
            sort: action.payload.sort,
            filter: action.payload.filter,
          },
        };

      case getType(actions.SEARCH.failure):
        return {
          ...draft,
          status: SearchStatus.none,
          request: {
            ...draft.request,
            limit: state.lastGoodRequest.limit,
            offset: state.lastGoodRequest.offset,
            sort: state.lastGoodRequest.sort,
            filter: {
              ...draft.request.filter,
              assessment_statuses:
                state.lastGoodRequest.filter.assessment_statuses,
              exclude_assets: state.lastGoodRequest.filter.exclude_assets,
              organisations: state.lastGoodRequest.filter.organisations,
            },
          },
        };

      case getType(actions.SEARCH.success):
        draft.error = undefined;
        draft.status = SearchStatus.none;
        draft.results.total = action.payload.total;
        if (
          state.results.asset_ids.join(',') !==
          action.payload.asset_ids.join(',')
        ) {
          if (state.request.offset === 0) {
            draft.results.asset_ids.splice(0, state.results.asset_ids.length);
          }
          action.payload.asset_ids.forEach((id) => {
            draft.results.asset_ids.push(id);
          });
        }

        for (const assetId of Object.keys(action.payload.assets)) {
          if (!state.results.assets[assetId]) {
            draft.results.assets[assetId] = action.payload.assets[assetId]!;
          }
        }

        for (const organisationId of Object.keys(
          action.payload.organisations,
        )) {
          if (!state.results.organisations[organisationId]) {
            draft.results.organisations[organisationId] =
              action.payload.organisations[organisationId]!;
          }
        }
        return draft;

      case getType(actions.CACHE_LAST_GOOD_REQUEST):
        return { ...draft, lastGoodRequest: action.payload };

      case getType(actions.SET_OFFSET):
        return {
          ...draft,
          request: { ...draft.request, offset: action.payload },
        };

      case getType(actions.SET_LIMIT):
        return {
          ...draft,
          request: { ...draft.request, limit: action.payload },
        };

      case getType(actions.SET_SORT):
        return {
          ...draft,
          results: initialState.results,
          request: {
            ...draft.request,
            offset: 0,
            sort: action.payload,
          },
        };

      case getType(actions.SET_FILTER):
        draft.results = initialState.results;
        draft.request.offset = 0;

        for (const key of Object.keys(action.payload)) {
          const k = key as keyof typeof action.payload;
          const value = action.payload[k];
          if (value !== undefined) {
            draft.request.filter[k] = value as string & string[];
          }
        }
        return draft;

      case getType(actions.TOGGLE_CHOSEN_SEARCH_ASSET):
        return {
          ...draft,
          chosen: draft.chosen.includes(action.payload)
            ? without(draft.chosen, action.payload)
            : [...draft.chosen, action.payload],
        };

      case getType(actions.CLEAR_CHOSEN_SEARCH_ASSETS):
        return { ...draft, chosen: initialState.chosen };

      case getType(actions.REFETCH_SEARCH):
        return {
          ...draft,
          request: {
            ...draft.request,
            offset: 0,
          },
          chosen: initialState.chosen,
        };

      case getType(actions.CLOSE_CLOUD_ASSETS):
        return { ...draft, opened: false, status: SearchStatus.none };

      case getType(actions.UNSET_CLOUD_ASSETS):
      case getType(SIGN_OUT.success):
        return initialState;

      default:
        return state;
    }
  });
